import { useRef, useState } from 'react';

import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useFormikForm } from '#/common/hooks';
import { apiEndpoints, usePostMutation } from '#/services';
import { FIELD_TYPES, MAX_FILE_SIZE, VALIDATION_MESSAGES } from '#/utils/constants';
import { getFileNameFromUrl, replaceWhiteSpaceWithUnderscore, toSnakeCase } from '#/utils/helperFunctions';

export function useLeadForm(chatbotData, faqQuestionId, setFaqQuestionId) {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [fileFieldName, setFileFieldName] = useState('');
  const [isFieldSubmit, setIsFieldSubmit] = useState(false);
  const [fileQueue, setFileQueue] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);

  const fileRef = useRef(null);

  const faqs = chatbotData?.data?.setting?.faqs || [];
  const renderLeadForm = faqs?.find(item => item?.id === faqQuestionId);

  const onUploadFileSuccess = ({ data }) => {
    setUploadedFiles(prev => {
      const existing = prev[fileFieldName] || [];
      return {
        ...prev,
        [fileFieldName]: [...existing, data?.file],
      };
    });

    processNextFile();
  };

  const onError = ({ response: { data } = {} }) => {
    const errorMessage =
      data?.image?.[0] ||
      data?.detail ||
      data?.non_field_errors?.[0] ||
      data?.chatbot_lead?.[0] ||
      VALIDATION_MESSAGES.SUBMISSION_ERROR;

    toast.error(errorMessage);
    processNextFile();
  };

  const { mutate: uploadFile, isPending: isUploadFileLoading } = usePostMutation({
    url: apiEndpoints.UPLOAD_FILE,
    onSuccess: onUploadFileSuccess,
    onError,
  });

  const { mutate: onLeadSubmit, isPending: isLeadSubmitLoading } = usePostMutation({
    url: apiEndpoints.LEAD_RECORD,
    onSuccess: () => {
      setIsFieldSubmit(false);
      handleLeadFormClose();
    },
    onError,
  });

  const generateInitialValues = fields =>
    fields?.reduce((acc, field) => {
      if (field?.label) acc[field.label] = '';
      return acc;
    }, {}) || {};

  const generateValidationSchema = fields => {
    const schema = {};
    fields?.forEach(({ label, type, isRequired }) => {
      if (!label) return;
      const lowerType = type?.toLowerCase();

      let validator;

      switch (lowerType) {
      case FIELD_TYPES.EMAIL:
        validator = Yup.string().email(VALIDATION_MESSAGES.INVALID_EMAIL);
        break;
      case FIELD_TYPES.NUMBER:
        validator = Yup.string().matches(/^\d+$/, VALIDATION_MESSAGES.NUMERIC_ONLY);
        break;
      case FIELD_TYPES.UPLOAD:
        validator = Yup.mixed().test(
          'fileRequired',
          VALIDATION_MESSAGES.REQUIRED,
          value => !isRequired || (value && value.length > 0)
        );
        break;
      default:
        validator = Yup.string();
      }

      if (isRequired && lowerType !== FIELD_TYPES.UPLOAD) {
        validator = validator.required(VALIDATION_MESSAGES.REQUIRED);
      }

      schema[label] = validator;
    });
    return Yup.object().shape(schema);
  };

  const onLeadFormSubmit = values => {
    const payload = {
      chatbotLead: renderLeadForm?.lead?.id,
      response: toSnakeCase({ ...values, ...uploadedFiles }),
    };
    onLeadSubmit({ payload });
    resetForm();
    setIsFieldSubmit(true);
    setUploadedFiles({});
  };

  const leadFormik = useFormikForm(
    generateInitialValues(renderLeadForm?.lead?.fields),
    onLeadFormSubmit,
    generateValidationSchema(renderLeadForm?.lead?.fields)
  );

  const processNextFile = () => {
    const nextIndex = currentFileIndex + 1;
    setCurrentFileIndex(nextIndex);

    if (nextIndex < fileQueue.length) {
      const { file, fieldName } = fileQueue[nextIndex];
      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(fieldName);
      uploadFile({ payload: formData });
    } else {
      setIsProcessingFiles(false);
      setFileQueue([]);
      setCurrentFileIndex(0);
    }
  };

  const handleFileChange = fieldName => e => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    const newFileQueue = [];
    let hasOversized = false;
    let hasDuplicate = false;

    const existingFileNames = uploadedFiles[fieldName]?.map(item => getFileNameFromUrl(item));

    for (let file of files) {
      const fileName = replaceWhiteSpaceWithUnderscore(file.name);
      if (existingFileNames?.includes(fileName)) {
        hasDuplicate = true;
        toast.error(VALIDATION_MESSAGES.DUPLICATE_FILE);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        hasOversized = true;
        toast.error(`${file.name} - ${VALIDATION_MESSAGES.FILE_TOO_LARGE}`);
        continue;
      }

      newFileQueue.push({ file, fieldName });
    }

    if ((hasOversized || hasDuplicate) && newFileQueue.length === 0) {
      setIsFieldSubmit(!isFieldSubmit);
      return;
    }

    setFileQueue(prev => [...prev, ...newFileQueue]);
    setCurrentFileIndex(fileQueue.length);
    setIsProcessingFiles(true);

    leadFormik.setFieldValue(fieldName, files);

    if (newFileQueue.length > 0) {
      const { file, fieldName: firstFieldName } = newFileQueue[0];
      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(firstFieldName);
      uploadFile({ payload: formData });
    }

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleRemoveFile = (fieldName, fileUrl) => () => {
    setUploadedFiles(prev => {
      const existing = prev[fieldName];
      if (!existing) return prev;

      const updated = existing.filter(f => f !== fileUrl);
      if (updated.length === 0) {
        const copy = { ...prev };
        delete copy[fieldName];
        leadFormik.setFieldValue(fieldName, '');
        return copy;
      }

      leadFormik.setFieldValue(fieldName, updated);
      return { ...prev, [fieldName]: updated };
    });
  };

  const handleTelInputChange = fieldName => e => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      leadFormik.setFieldValue(fieldName, value);
    }
  };

  const handleSelectChange = (fieldName, value) => {
    leadFormik.setFieldValue(fieldName, value);
  };

  const handleLeadFormClose = () => {
    setFaqQuestionId('');
    setUploadedFiles({});
  };

  return {
    faqQuestionId,
    uploadedFiles,
    setUploadedFiles,
    isFieldSubmit,
    setIsFieldSubmit,
    leadFormik,
    isUploadFileLoading,
    handleFileChange,
    handleTelInputChange,
    handleLeadFormClose,
    handleRemoveFile,
    handleSelectChange,
    renderLeadForm,
    isProcessingFiles,
    fileRef,
    isLeadSubmitLoading,
  };
}
